// ══════════════════════════════════════════════════════════════════════════════════════════════
// LE WIZARD DE CAMPAGNE — transposé du DOM de la maquette, mesures comprises.
//
// ⚠️ TROISIÈME ÉCRITURE, ET LA LEÇON EST LA MÊME QUE POUR « PARAMÈTRES DE CAISSE » (2026-09-07). La
// 1ʳᵉ version inventait un formulaire à plat. La 2ᵉ suivait le PLAN rendu par
// `extract-maquette-screen.mjs` — mais cet outil retire les styles ET aplatit les `sc-if` : j'y ai lu
// trois étapes là où le dessin en compte CINQ, et aucune des mesures. Les valeurs ci-dessous sont
// RELEVÉES du DOM inline (`scripts/…/dom-brut.mjs`) : carte 640 px (920 sur l'étape e-mail), en-tête
// 16,5/700 avec ses points de progression 22×5, corps `20px 22px`, pied `14px 22px` sur `ink-50`,
// étiquettes 11/600 `ink-500`, champs h38/h40, tuiles de canal 40×40 + pastille de crédits.
//
// ⚠️ CINQ ÉTAPES, ET LEUR NOMBRE DÉPEND DU CANAL : le message SMS et l'e-mail sont deux étapes
// DISTINCTES, montrées seulement si le canal correspondant est retenu. C'est ce qui fait qu'on ne
// rédige jamais un e-mail pour une campagne SMS.
//
// ⚠️ CE QUI N'A PAS DE MOTEUR EST RENDU, MAIS DÉSACTIVÉ, AVEC SON MOTIF (demande utilisateur : « des
// boutons disabled sur les fonctions pas encore existantes »). C'est la forme du dessin sans la
// promesse — un bouton qui ouvrirait sur rien serait pire que son absence (§23.0).
// ══════════════════════════════════════════════════════════════════════════════════════════════
import { useCallback, useEffect, useId, useMemo, useRef, useState, type CSSProperties } from 'react';
import { SMS_STOP_MENTION, smsSegments, withStopMention } from '../../../server/src/marketing/sms-stop';
import { composeBody, escapeHtml, mergeHtml, mergeText } from '@orgazone/email-template';
import { Icon, showToast, Trans, EmailEditor, useOverlayPanel } from '@orgazone/ui-web';
import { api, type Campaign, type CampaignChannel, type CampaignCredits, type CampaignFilters, type CampaignInput, type CampaignRecurrence, type EmailTemplateInput, type EmailTemplateRow, type SavedAudience } from '../api';
import { useStoreScope } from '../store-context';
import { key, t, type DeclaredKey } from '../i18n';
import { channelLabel, channelReachability, channelUnit } from '../campaign-channels';
import { FID_TIER_DEFAULTS } from '@orgazone/sync-contract';
import { CRITERES_VIDES, EtapeAudience, criteresDe, filtresDe, type Criteres } from './marketing-audience';

const motif = (e: unknown): string => (e instanceof Error ? e.message : String(e));

/** Le compte de la tuile verte de l'audience, et la légende posée dessous. */
const AUDIENCE_FIGURE: CSSProperties = { fontSize: 'var(--t-display)', fontWeight: 800, color: 'var(--teal-800)', fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em' };
const AUDIENCE_CAPTION: CSSProperties = { fontSize: 'var(--t-note)', fontWeight: 700, color: 'var(--teal-800)' };

// ── Les mesures RELEVÉES du DOM de la maquette ────────────────────────────────────────────────
const etiq: CSSProperties = { display: 'block', fontSize: 'var(--t-meta)', fontWeight: 600, color: 'var(--ink-500)', marginBottom: 5 };
const champ38: CSSProperties = { width: '100%', height: 38, padding: '0 11px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', fontSize: 'var(--t-lead)', color: 'var(--ink-900)', boxSizing: 'border-box' };
const select38: CSSProperties = { ...champ38, padding: '0 10px' };
const note: CSSProperties = { display: 'flex', gap: 8, alignItems: 'flex-start', padding: '10px 12px', borderRadius: 'var(--r-ctrl)', background: 'var(--ink-50)', fontSize: 'var(--t-note)', color: 'var(--ink-500)', lineHeight: 1.55 };
const ambre: CSSProperties = { ...note, background: 'var(--amber-bg)', color: 'var(--amber-fg)' };
const rose: CSSProperties = { ...note, padding: '11px 13px', fontSize: 'var(--t-dense)', background: 'var(--rose-bg)', color: 'var(--rose-fg)' };
const colonne: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 11 };

/** Un dessin de la maquette, rendu à sa taille. */
function Ic({ d, size = 16, w = 1.9 }: { d: string; size?: number; w?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth={w} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d={d} />
    </svg>
  );
}

/** L'interrupteur 38×22 de la maquette — même géométrie que partout ailleurs au back-office. */
function Bascule({ on }: { on: boolean }) {
  return (
    <span style={{ position: 'relative', width: 38, height: 22, borderRadius: 'var(--r-pill)', flexShrink: 0, display: 'inline-block', background: on ? 'var(--teal-500)' : 'var(--ink-200)', transition: 'background .15s' }}>
      <span style={{ position: 'absolute', top: 3, left: on ? 19 : 3, width: 16, height: 16, borderRadius: 'var(--r-pill)', background: '#fff', boxShadow: '0 1px 3px rgba(12,14,16,.3)', transition: 'left .15s' }} />
    </span>
  );
}

const IC = {
  sms: 'M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v10z',
  wallet: 'M20 7H4a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2zM2 9l10-5 8 3M16 13h3',
  mail: 'M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2zM22 7l-10 6L2 7',
  panier: 'M6 6h15l-1.5 9h-12L5 3H2M9 20a1 1 0 1 0 0-2 1 1 0 0 0 0 2zM18 20a1 1 0 1 0 0-2 1 1 0 0 0 0 2z',
  alerte: 'M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0zM12 9v4M12 17h.01',
  info: 'M12 11v5M12 8v.5',
  eclair: 'M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z',
  agenda: 'M8 2v4M16 2v4M3 9h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z',
  boucle: 'M3 12a9 9 0 1 0 3-6.7L3 8M3 3v5h5',
  image: 'M21 16l-5-5-7 7',
  croix: 'M6 6l12 12M18 6L6 18',
} as const;

/**
 * Le canal d’une campagne — LE TYPE DU SERVEUR, jamais un vocabulaire d’écran.
 *
 * ⚠️ L’assistant en tenait un à lui (`'sms' | 'wallet' | 'email'`), à traduire avant chaque appel.
 * Une correspondance de plus est une occasion de plus de diverger — et c’est exactement ce qui était
 * arrivé : `wallet` y était dessiné pendant que le serveur ne connaissait que deux canaux. Le mot du
 * serveur voyage désormais tel quel ; seul son AFFICHAGE se traduit (`libelleCanal`).
 */
type Canal = CampaignChannel;

/**
 * LES CHAMPS DE FUSION — des JETONS, pas des textes (Forme CQ).
 *
 * ⚠️ LE SERVEUR LES RECONNAÎT À LEUR NOM FRANÇAIS au moment de l'envoi : traduits, `{prénom}`
 *    deviendrait `{first name}` dans le message composé et ne serait remplacé par rien — le
 *    destinataire lirait le jeton. Ils s'affichent pour être COPIÉS, pas pour être lus.
 */
const FUSION = ['{prénom}', '{points}', '{palier}', '{bon}'] as const;

/**
 * 🔴 LES CHAMPS QUE LE SERVEUR SAIT VRAIMENT REMPLIR.
 *
 * ⚠️ 🔴 `{bon}` N'EN FAIT PAS PARTIE, et sa pastille est donc DÉSACTIVÉE avec son motif. Joindre un
 * bon suppose un moteur de titres par destinataire qui n’existe pas — le serveur REFUSE désormais
 * un corps qui le cite, à l'écriture. Laisser la pastille cliquable ferait composer un message que
 * l'enregistrement rejetterait, ou pire : avant la garde, il partait EN TOUTES LETTRES au client.
 */
export const FUSION_REMPLIE = new Set(['{prénom}', '{points}', '{palier}']);

/**
 * 🔴 LES VALEURS D'EXEMPLE DE L'APERÇU — UNE table, d'où tout le reste se dérive.
 *
 * ⚠️ 🔴 L'APERÇU DOIT MONTRER LE CAS NORMAL, pas le repli. Rendre le corps avec les valeurs de repli
 * (chaîne vide pour le prénom) ferait relire au commerçant un message amputé, et il réécrirait sa
 * phrase pour un défaut qui n'existe que dans l'aperçu.
 *
 * ⚠️ UNE SEULE SOURCE pour les trois usages — les champs remis au moteur, les valeurs substituées et
 * la phrase qui dit lesquelles. Trois listes « à peu près pareilles » divergeraient au premier jeton
 * ajouté, et l'aperçu substituerait alors un jeton que la phrase ne nomme pas (Forme A).
 */
// ⚠️ UNE UNION, PAS UN DRAPEAU À CÔTÉ D’UN TYPE COMMUN : un exemple traduit est une CLÉ, un exemple de données (un
//    nombre de points, un palier du magasin) est un TEXTE — le second ne doit jamais passer par `t()`, ni le premier
//    s’afficher sans lui.
export const APERCU: ReadonlyArray<{ token: string; sample: DeclaredKey; translated: true } | { token: string; sample: string; translated: false }> = [
  // ⚠️ 🔴 LE JETON S'ÉCRIT AVEC SES ACCOLADES, et son nom NE SE TRADUIT PAS : c'est la clé que le
  //    serveur reconnaît. Traduit, `{first name}` ne serait remplacé par rien et le destinataire
  //    lirait le jeton (Forme CQ). La VALEUR, elle, est lue par un humain.
  { token: '{prénom}', sample: key('Léa'), translated: true },
  { token: '{points}', sample: '120', translated: false },
  // ⚠️ 🔴 LE PALIER EST UNE DONNÉE, PAS UN TEXTE, ET DEUX RAISONS L'INTERDISENT À LA TRADUCTION.
  //    Ses libellés par défaut vivent au contrat et le commerçant les renomme à l'écran : les
  //    recopier ici en ferait une SECONDE table, qui montrerait un palier inexistant le jour où la
  //    première change. Et passer une donnée à `t()` l'expose à l'HOMONYMIE — le français étant la
  //    clé, un mot qui existe ailleurs au catalogue hériterait en silence de l'autre sens.
  { token: '{palier}', sample: FID_TIER_DEFAULTS.tiers[1]!.l, translated: false },
];

/**
 * ⚠️ 🔴 RÉSOLU AU RENDU, JAMAIS AU CHARGEMENT DU MODULE. Appeler `t()` à côté de la table figerait le
 * mot dans la langue qui avait cours à l'import — et la langue du compte n'arrive qu'après.
 */
function exemplesApercu(): { champs: { name: string; description: string; fallback: string }[]; valeurs: Record<string, string>; phrase: string } {
  const resolu = APERCU.map((e) => ({ nom: e.token.slice(1, -1), valeur: e.translated ? t(e.sample) : e.sample }));
  return {
    // ⚠️ Descriptions et replis VIDES : l'aperçu ne compose pas, il substitue — rien d'autre n'est lu.
    champs: resolu.map(({ nom }) => ({ name: nom, description: '', fallback: '' })),
    valeurs: Object.fromEntries(resolu.map(({ nom, valeur }) => [nom, valeur])),
    phrase: resolu.map(({ nom, valeur }) => `{${nom}} = ${valeur}`).join(', '),
  };
}

/**
 * CE QUE LA CRÉATION ENVOIE AU SERVEUR — assemblé ici, hors du composant, pour être éprouvable.
 *
 * ⚠️ 🔴 LE PARAMÈTRE S'APPELLE `composedBody` ET NON « corps », DÉLIBÉRÉMENT. C'est précisément ici
 * que le titre et l'appel à l'action étaient perdus : le corps BRUT de l'éditeur était envoyé, sans
 * eux. Un nom qui dit « composé » rend la faute visible au site d'appel, là où « corps » la rendait
 * plausible.
 *
 * ⚠️ UN MESSAGE COURT (SMS, wallet) N'EST PAS DU BALISAGE : il part tel quel, sans composition.
 * ⚠️ ET L'OBJET N'EXISTE QUE POUR L'E-MAIL — un SMS n'en a pas, et la colonne resterait à `null`.
 */
export function campaignInput(p: {
  name: string; channel: CampaignChannel; short: boolean;
  shortMessage: string; composedBody: string; subject: string | null;
}): CampaignInput {
  return {
    name: p.name.trim(),
    channel: p.channel,
    body: p.short ? p.shortMessage : p.composedBody,
    ...(p.subject !== null ? { subject: p.subject } : {}),
  };
}


/** Ce qu'un modèle pose dans l'e-mail de l'assistant — objet, titre, corps et bouton. */
export interface ContenuEmail {
  objet: string;
  titre: string;
  corps: string;
  boutonOn: boolean;
  boutonL: string;
  boutonUrl: string;
}

/**
 * LE CONTENU D'UN MODÈLE — intégré (« annonce », « vierge ») ou enregistré dans la bibliothèque (`saved:<id>`).
 *
 * ⚠️ 🔴 CHOISIR UN MODÈLE REMPLIT L'E-MAIL. Le sélecteur gardait le choix sans rien en faire : il
 * annonçait une fonction (« partir d'un gabarit ») qui ne faisait rien (§23.0, bouton qui fait semblant).
 * ⚠️ « bon » REND `null` : son texte promet un bon « déjà sur votre carte », et joindre un bon n'existe
 * pas encore — le proposer ferait écrire à des clients une promesse que personne ne tiendra.
 * ⚠️ UN GABARIT DE LA BIBLIOTHÈQUE N'A PAS DE TITRE : une campagne part sans habillage, donc un titre
 * enregistré l'a été DANS le corps (voir `gabaritDeCampagne`) — il revient avec lui.
 * ⚠️ Les textes intégrés sont TRADUITS : c'est le commerçant qui les envoie, dans sa langue. Les jetons
 * (`{prénom}`) restent hors des clés : un jeton ne se traduit jamais.
 */
export function contenuDeModele(
  choix: string, magasin: string, bibliotheque: readonly EmailTemplateRow[],
): ContenuEmail | null {
  if (choix === 'vierge') {
    return { objet: '', titre: '', corps: '', boutonOn: true, boutonL: t('Découvrir'), boutonUrl: '' };
  }
  if (choix === 'annonce') {
    return {
      objet: magasin.trim() === '' ? t('Du nouveau en boutique') : t('Du nouveau chez {v}', { v: magasin.trim() }),
      // ⚠️ LE JETON VIT DANS LA CLÉ ET Y RESTE TEL QUEL dans chaque traduction : le moteur ne remplace que les
      //    trous qu'on lui passe, et un jeton ne se traduit jamais.
      titre: t('Bonjour {prénom},'),
      corps: `<div>${escapeHtml(t('Une nouveauté vous attend en boutique cette semaine — passez nous voir.'))}</div>`,
      boutonOn: true, boutonL: t('Découvrir'), boutonUrl: '',
    };
  }
  if (choix.startsWith('saved:')) {
    const g = bibliotheque.find((r) => r.id === choix.slice('saved:'.length));
    if (!g) return null;
    return {
      objet: g.subject, titre: '', corps: g.body, boutonOn: g.button !== null,
      boutonL: g.button?.label ?? t('Découvrir'), boutonUrl: g.button?.url ?? '',
    };
  }
  return null;
}

/**
 * LE GABARIT QU'ENREGISTRE L'ASSISTANT — ce que la campagne envoie, réutilisable tel quel.
 * ⚠️ 🔴 LE TITRE ENTRE DANS LE CORPS, comme à l'envoi (`composeBody`) : une campagne n'a pas de titre de carte,
 * et la bibliothèque le refuse pour elle. Le jeter ferait enregistrer un message qui n'est pas celui parti.
 * ⚠️ Le BOUTON reste à part, avec ses deux moitiés : c'est le cœur qui juge, et l'écran dit son refus.
 */
export function gabaritDeCampagne(nom: string, c: ContenuEmail): EmailTemplateInput {
  return {
    name: nom.trim(),
    purpose: 'campaign',
    subject: c.objet,
    title: null,
    body: composeBody({ body: c.corps, title: c.titre, button: null }),
    button: c.boutonOn ? { label: c.boutonL, url: c.boutonUrl } : null,
  };
}

/**
 * CE QUE L’ASSISTANT ANNONCE AVANT D’ENGAGER DES MILLIERS DE MESSAGES.
 *
 * ⚠️ 🔴 LE COMPTE PORTE SUR LE CORPS RÉEL, MENTION LÉGALE COMPRISE — jamais sur le texte saisi. La
 * mention est ajoutée à l’envoi : la compter à part ferait annoncer UN segment là où DEUX partent, et
 * sur 20 000 destinataires ce sont 20 000 SMS facturés que personne n’avait vus venir.
 *
 * ⚠️ ELLE EST EXPORTÉE POUR ÊTRE GARDÉE. Une phrase composée dans le JSX ne peut être éprouvée que
 * par un rendu ; celle-ci se lit directement, et c’est la seule information sur laquelle l’exploitant
 * fonde une dépense.
 */
/**
 * L'échéance, telle qu'un humain la lit — ou une phrase honnête quand il n'y en a pas.
 * ⚠️ RENDUE DANS LE FUSEAU DU NAVIGATEUR, et c’est acceptable ici : c’est une CONFIRMATION lue
 * juste après le clic, pas la donnée qui commande l’envoi. Ce qui commande est l’heure locale du
 * magasin, et elle vit côté serveur.
 */
function quandLisible(at: number | null): string {
  if (at === null) return t('une date déjà passée — vérifiez la saisie');
  return new Date(at).toLocaleString('fr-FR', { dateStyle: 'long', timeStyle: 'short' });
}

/** Ce que l’écran a saisi pour le MOMENT de l’envoi — la forme qui voyage jusqu’au serveur. */
export interface Moment {
  quand: 'now' | 'once' | 'repeat';
  /** Date locale `AAAA-MM-JJ` (envoi unique). */
  dateEnvoi: string;
  /** Heure locale `HH:MM`. */
  heureEnvoi: string;
  cadence: 'weekly' | 'monthly';
  jours: number[];
}

/**
 * CE QUE L’ÉCRAN A SAISI, dans la forme que le serveur attend — ou `null` si la saisie ne tient pas.
 *
 * ⚠️ 🔴 LE REFUS SE FAIT ICI, AVANT L’APPEL, et il NOMME ce qui manque. Un modèle « en service » qui
 * ne déclenche jamais est le pire des états : il s’affiche comme les autres, et rien ne dit qu’il ne
 * partira pas. Le serveur refuse aussi — les deux moitiés comptent, l’écran pour corriger tout de
 * suite, le serveur parce qu’il ne se fie jamais à ce qu’on lui envoie.
 *
 * ⚠️ ON ENVOIE DATE ET HEURE LOCALES, jamais un instant : le serveur les convertit avec le fuseau du
 * MAGASIN. Un instant calculé ici partirait à l’heure de celui qui a cliqué.
 */

export function recurrenceDeSaisie(m: Moment): CampaignRecurrence | null {
  const [h, min] = m.heureEnvoi.split(':').map(Number);
  if (!Number.isInteger(h) || !Number.isInteger(min)) return null;
  if (h! < 0 || h! > 23 || min! < 0 || min! > 59) return null;
  if (m.quand === 'once') {
    return m.dateEnvoi ? { kind: 'once', days: [], hour: h!, minute: min!, date: m.dateEnvoi } : null;
  }
  return m.jours.length ? { kind: m.cadence, days: [...m.jours].sort((a, b) => a - b), hour: h!, minute: min! } : null;
}

export function phraseCompteurSms(msg: string): string {
  const corps = withStopMention(msg);
  return t('{n} caractère — environ {sms} SMS par destinataire. La mention « {mention} » est ajoutée en fin de message : elle est obligatoire, et elle est comptée ici. Estimation : un caractère accentué hors table GSM peut doubler ce compte.',
    { n: corps.length, sms: smsSegments(corps), mention: SMS_STOP_MENTION });
}

/**
 * LE MÊME COMPTE, POUR UNE NOTIFICATION WALLET — et il dit surtout CE QU'ON N'AJOUTE PAS.
 *
 * ⚠️ 🔴 AUCUNE MENTION LÉGALE ICI, ET C'EST LE POINT. Elle est obligatoire sur un SMS de prospection
 * parce que le message n'offre aucun autre moyen de s'y opposer. Un pass en offre un, structurel : le
 * client le supprime ou coupe ses notifications depuis son téléphone, sans passer par nous. Coller
 * « STOP 36608 » sur une notification wallet y désignerait une porte fermée (§23.0).
 * ⚠️ NI DÉCOUPAGE NI SEGMENTS : un push part d'une pièce, il n'est pas facturé au bloc de 160
 * caractères. Reprendre la phrase du SMS annoncerait une dépense qui n'existe pas.
 */
export function phraseCompteurWallet(msg: string): string {
  return t('{n} caractère — une notification par porteur, sans découpage ni mention légale : le pass porte son propre moyen de se désabonner.', { n: msg.length });
}

/**
 * LE COMPTEUR DE L'ÉTAPE 2, PAR CANAL.
 * ⚠️ `null` pour l'e-mail, et ce n'est pas un oubli : il se compose à l'étape 3, avec son aperçu, et
 * n'a aucun compte de caractères à annoncer. Une table TOTALE oblige à le dire plutôt qu'à le
 * laisser tomber dans un `else`.
 */
export const COMPTEUR: Record<CampaignChannel, ((msg: string) => string) | null> = {
  sms: phraseCompteurSms,
  wallet_push: phraseCompteurWallet,
  email: null,
};

/** Ce que le serveur ne sait pas faire — le motif s'affiche au survol du contrôle désactivé. */
/**
 * CE QUI EST DESSINÉ MAIS N'A AUCUN MOTEUR — et POURQUOI, en une phrase lisible à l'écran.
 *
 * ⚠️ 🔴 IL EST EXPORTÉ, et ce n'est pas une commodité : le capping était écrit DEUX FOIS et les deux
 * versions se contredisaient — le pied de l'écran Marketing disait « à venir, aucune limite n'est
 * appliquée » pendant que cet assistant AFFIRMAIT « 3 messages max par client et par mois […] jamais
 * sur-sollicité ». Un seul texte, un seul endroit : c'est la seule façon qu'ils ne divergent plus.
 */
export const SANS_MOTEUR = {
  bon: key('Joindre un bon suppose d’émettre un titre PAR DESTINATAIRE au registre « Titres & bons », avec un code unique. Ce mécanisme n’existe pas encore.'),
  image: key('Insérer une image demande un hébergement des pièces jointes — un destinataire ne verra jamais une image collée dans le corps.'),
  credits: key('La commande de crédits se fait chez l’opérateur : aucune adresse de boutique n’est enregistrée dans ce back-office.'),
  /**
   * ⚠️ LE DÉCLENCHEUR PAR PERSONNE N’EXISTE TOUJOURS PAS, et c’est la moitié qu’on confondrait.
   * Un modèle RÉCURRENT part à une heure fixe, à tous ceux qui correspondent aux critères ce jour-là.
   * Un déclencheur par PERSONNE partirait au moment où l’état de CE client change — sa carte
   * s’inscrit, sa cagnotte expire dans huit jours, il n’est plus venu depuis six mois. Les deux se
   * ressemblent à l’écran et ne se construisent pas du tout pareil : le second demande de repérer un
   * CHANGEMENT, donc une trace de ce qui a déjà été envoyé à chacun.
   */
  parPersonne: key('Se déclencher client par client (bienvenue, cagnotte qui expire, client inactif) demande de repérer un CHANGEMENT d’état par personne, et de retenir ce qui a déjà été envoyé à chacune. La répétition à heure fixe, elle, existe : c’est « Répéter » ci-dessus.'),
  capping: key('Plafonner ce qu’un client peut recevoir par mois demande un compteur par personne, tous canaux confondus. Aujourd’hui, AUCUNE limite n’est appliquée.'),
} as const;

/**
 * LES JETONS TELS QUE L'ÉDITEUR PARTAGÉ LES ATTEND — DÉRIVÉS des deux listes du haut, jamais
 * recopiés : une troisième liste divergerait au premier champ ajouté.
 *
 * ⚠️ 🔴 DÉCLARÉE **APRÈS** `SANS_MOTEUR`, ET C'EST UNE CONTRAINTE. Une constante de module qui lit une
 * autre constante déclarée PLUS BAS lève à l'IMPORT (zone morte temporelle) : l'écran entier cesse de
 * se charger, et sept bancs tombent avec lui. Le rapprocher de `FUSION` par confort de lecture a coûté
 * exactement ça.
 */
const CHAMPS_EDITEUR = FUSION.map((token) => (
  FUSION_REMPLIE.has(token) ? { token } : { token, reason: SANS_MOTEUR.bon }
));

/**
 * À QUOI SERT UN GROUPE TÉMOIN — la phrase que lit la personne qui décide, pas le développeur.
 *
 * ⚠️ 🔴 ELLE EXISTE PARCE QUE LA MÉCANIQUE NE S'EXPLIQUE PAS TOUTE SEULE. Le témoin a été construit,
 * éprouvé et documenté dans la spec — et la première question posée devant l'écran a été « je sais
 * toujours pas à quoi ça sert ». Une option qu'on n'ose pas cocher parce qu'on ignore ce qu'elle
 * achète n'est pas vraiment offerte : c'est la même absence que le bouton grisé sans motif, par
 * l'autre bout.
 *
 * ⚠️ ELLE DIT LE MÉCANISME, PAS LE MOT. « Mesure d'incrémentalité » ne s'explique pas ; « une partie
 * de ces clients serait revenue sans rien recevoir » s'explique tout seul. Le premier est du
 * vocabulaire de métier, le second est la raison.
 *
 * ⚠️ ELLE EST VISIBLE TANT QU'AUCUN TÉMOIN N'EST DEMANDÉ : c'est à ce moment-là qu'on doit pouvoir décider d'en
 * prendre un.
 * ⚠️ LE DESSIN DU 28/10 PROPOSAIT UNE AUTRE PHRASE, sans la décision (« s'il faut la refaire ») : celle-ci répond à la
 *    question posée devant l'écran, c'est une décision de production que la maquette porte (`CONVENTIONS-PRODUCTION`).
 */
export const POURQUOI_TEMOIN =
  key('Une partie de vos clients serait revenue sans rien recevoir. En n’envoyant rien à quelques-uns, on compare les deux groupes et on sait ce que la campagne a vraiment rapporté — et donc s’il faut la refaire.');

/**
 * CE QUE DEVIENNENT LES TÉMOINS, une fois la part choisie — la phrase du dessin.
 * ⚠️ SANS DURÉE : le dessin écrivait « quinze jours après l'envoi », or la fenêtre d'attribution est de quatorze
 *    (`ATTRIBUTION_WINDOW_DAYS`) — et elle voyage avec le résultat, qui la nomme. Un chiffre recopié ici mentirait au
 *    premier changement de la fenêtre.
 */
export const TEMOINS_RETENUS =
  key('Tirés au hasard, ils ne reçoivent rien et ne consomment aucun crédit. Après l’envoi, leurs achats sont comparés à ceux des servis, sur la même fenêtre.');

/**
 * COMMENT L'AUDIENCE SE PARTAGE, et ce que l'exploitant a le droit d'en attendre.
 *
 * ⚠️ 🔴 LE NOMBRE QUI COMPTE EST CELUI QUI NE PARTIRA PAS. Choisir « 10 % de témoin » se lit comme un
 * réglage anodin ; « 124 clients ne recevront pas l'offre » est la même chose dite honnêtement, et
 * c'est la seule formulation sur laquelle on peut décider. L'écran affiche donc les deux nombres, pas
 * le pourcentage seul.
 *
 * ⚠️ UN TÉMOIN TROP PETIT NE CONCLURA JAMAIS, et il faut le dire AVANT l'envoi — après, il est trop
 * tard : les messages n'ont pas été envoyés ET la mesure ne dira rien. Le seuil vient du SERVEUR
 * (`minHoldout`), dérivé du seuil statistique du calcul d'écart ; le recopier ici en ferait une
 * seconde vérité qui survivrait au jour où le calcul change.
 *
 * ⚠️ ET C'EST UN ORDRE DE GRANDEUR, PAS UNE PROMESSE : le seuil suppose un taux d'achat. Un témoin
 * assez grand peut encore ne rien conclure si les clients achètent moins que supposé — le verdict
 * vient toujours après coup, des comptes réels. L'avertissement dit « risque de ne rien conclure »,
 * jamais « ne conclura pas ».
 */
export function partageDAudience(total: number | null, pct: number, minTemoin: number | null): {
  temoins: number; servis: number; avertissement: string | null;
} {
  if (total === null || pct <= 0) return { temoins: 0, servis: total ?? 0, avertissement: null };
  const temoins = Math.round((total * pct) / 100);
  const servis = total - temoins;
  if (temoins === 0) {
    // ⚠️ Une part demandée qui ne retient PERSONNE n'est pas un témoin : le dire évite de croire
    //    qu'une mesure aura lieu. Arrive dès que l'audience est minuscule.
    return { temoins, servis, avertissement: t('Audience trop petite : ce pourcentage ne retient personne — il n’y aura aucun groupe témoin.') };
  }
  if (servis === 0) {
    return { temoins, servis, avertissement: t('Tout le monde serait témoin : personne ne recevrait le message. Baissez la part, ou élargissez l’audience.') };
  }
  if (minTemoin !== null && temoins < minTemoin) {
    return {
      temoins,
      servis,
      // ⚠️ LE COMPTE S'APPELLE `{n}` : c'est le seul trou que le moteur accorde — sous un autre nom, il
      //    écrirait « 1 témoins » pour toujours, dans toutes les langues (Forme DD).
      avertissement: t('{n} témoin : c’est peu, et la mesure risque de ne rien conclure (comptez {minimum} environ). Ces clients ne recevront rien pour autant.', { n: temoins, minimum: t('{n} témoin', { n: minTemoin }) }),
    };
  }
  return { temoins, servis, avertissement: null };
}

export function WizardCampagne({ open, onClose, onCree }: {
  open: boolean; onClose: () => void; onCree: (c: Campaign) => void;
}) {
  /** Le préfixe des identifiants qui relient chaque champ au texte qui le présente. */
  const fieldId = useId();
  const [etape, setEtape] = useState(1);
  const [nom, setNom] = useState('');
  const [canal, setCanal] = useState<Canal>('email');
  const [msg, setMsg] = useState('');
  const magasin = useStoreScope().view?.active?.name ?? '';
  // ⚠️ L'ASSISTANT S'OUVRE SUR « Annonce simple » DÉJÀ REMPLI, comme le dessin : un sélecteur qui annonce un
  //    modèle au-dessus de champs vides montrerait deux choses qui ne s'accordent pas.
  const [depart] = useState(() => contenuDeModele('annonce', magasin, [])!);
  const [objet, setObjet] = useState(depart.objet);
  const [titre, setTitre] = useState(depart.titre);
  const [gabarit, setGabarit] = useState('annonce');
  /** Remonte l'éditeur — il est non contrôlé, donc un AUTRE corps exige un autre montage. */
  const [corpsCle, setCorpsCle] = useState(0);
  /** Les gabarits de campagne du magasin — relus à l'ouverture de l'assistant. */
  const [bibliotheque, setBibliotheque] = useState<EmailTemplateRow[]>([]);
  const [bibliErreur, setBibliErreur] = useState<string | null>(null);
  const [enregistrerGabarit, setEnregistrerGabarit] = useState(false);
  const [nomGabarit, setNomGabarit] = useState('');
  // ⚠️ LE CORPS EST TENU EN ÉTAT, pas relu du DOM : l'aperçu doit se redessiner à la frappe, et un
  //    `ref.innerHTML` lu pendant le rendu ne déclenche aucun rendu — l'aperçu resterait figé.
  const [corps, setCorps] = useState(depart.corps);
  const [boutonOn, setBoutonOn] = useState(depart.boutonOn);
  const [boutonL, setBoutonL] = useState(depart.boutonL);
  const [boutonUrl, setBoutonUrl] = useState(depart.boutonUrl);

  /** Pose un modèle dans l'e-mail — ou garde ce qui est écrit si le modèle n'a pas de contenu à poser. */
  const appliquerModele = (choix: string) => {
    setGabarit(choix);
    const c = contenuDeModele(choix, magasin, bibliotheque);
    if (!c) return;
    setObjet(c.objet); setTitre(c.titre); setCorps(c.corps);
    setBoutonOn(c.boutonOn); setBoutonL(c.boutonL); setBoutonUrl(c.boutonUrl);
    setCorpsCle((k) => k + 1);
  };

  /**
   * ENREGISTRE L'E-MAIL DANS LA BIBLIOTHÈQUE, si on l'a demandé — après la campagne, jamais à sa place.
   * ⚠️ SON ÉCHEC N'EMPORTE PAS LA CAMPAGNE, et il se DIT : même règle que l'audience enregistrée. Un nom déjà
   * pris se corrige au prochain envoi ; une campagne perdue se recommence en entier.
   */
  const enregistrerDansBibliotheque = async () => {
    if (!email || !enregistrerGabarit || nomGabarit.trim() === '') return;
    try {
      const { record } = await api.createEmailTemplate(gabaritDeCampagne(nomGabarit, {
        objet, titre, corps, boutonOn, boutonL, boutonUrl,
      }));
      setBibliotheque((l) => [...l, record]);
    } catch (e) {
      showToast(t('Campagne créée, mais le gabarit n’a pas été enregistré : {v}', { v: motif(e) }), 'err');
    }
  };
  /**
   * 🔴 LE CORPS TEL QU'IL SERA STOCKÉ ET ENVOYÉ — titre et appel à l'action compris.
   *
   * ⚠️ 🔴 UNE SEULE DÉFINITION POUR L'APERÇU ET POUR L'ENREGISTREMENT. Les composer séparément est
   * exactement ce qui a produit le défaut : l'aperçu dessinait un titre et un bouton que la création
   * jetait, et rien ne pouvait le voir puisque chaque moitié était cohérente avec elle-même.
   */
  const corpsComplet = useMemo(
    () => composeBody({ body: corps, title: titre, button: boutonOn ? { label: boutonL, url: boutonUrl } : null }),
    [corps, titre, boutonOn, boutonL, boutonUrl],
  );
  // ⚠️ Résolu au RENDU (voir `exemplesApercu`) : au chargement du module, la langue du compte
  //    n’est pas encore connue et le mot serait figé dans celle de la plateforme.
  const apercu = useMemo(() => exemplesApercu(), []);
  const [crit, setCrit] = useState<Criteres>(CRITERES_VIDES);
  /** La part réservée au groupe témoin, en pourcentage — zéro tant qu'on ne la demande pas. */
  const [pctTemoin, setPctTemoin] = useState(0);
  /** Le plus petit témoin qui puisse conclure — SERVI par le serveur, jamais recopié ici. */
  const [minTemoin, setMinTemoin] = useState<number | null>(null);
  /** Les audiences enregistrées du magasin — rechargées à chaque ouverture de l'assistant. */
  const [audiences, setAudiences] = useState<SavedAudience[]>([]);
  /** Ce que la dernière audience rechargée portait et que cet écran ne sait pas rendre. */
  const [ignores, setIgnores] = useState<string[]>([]);
  /** L'audience enregistrée dont on est reparti — oubliée dès qu'on retouche un critère. */
  const [audienceChoisie, setAudienceChoisie] = useState<SavedAudience | null>(null);
  /** Enregistrer l'audience composée, sous ce nom — l'interrupteur du dessin. */
  const [enregistrerAudience, setEnregistrerAudience] = useState(false);
  const [nomAudience, setNomAudience] = useState('');
  /** Quand ça part : tout de suite, une fois à date fixe, ou en répétition. */
  const [quand, setQuand] = useState<'now' | 'once' | 'repeat'>('now');
  /** ⚠️ DATE ET HEURE LOCALES DU MAGASIN : le serveur les convertit avec `store.timezone`. Calculer
   *  un instant ici ferait partir le message à l'heure de celui qui a cliqué. */
  const [dateEnvoi, setDateEnvoi] = useState('');
  const [heureEnvoi, setHeureEnvoi] = useState('09:00');
  /** `weekly` = jours de semaine (1 = lundi) · `monthly` = quantièmes du mois. */
  const [cadence, setCadence] = useState<'weekly' | 'monthly'>('monthly');
  const [jours, setJours] = useState<number[]>([1]);
  const [total, setTotal] = useState<number | null>(null);
  const [credits, setCredits] = useState<CampaignCredits | null>(null);
  const [busy, setBusy] = useState(false);
  const zoneMsg = useRef<HTMLTextAreaElement | null>(null);

  const sms = canal === 'sms';
  const email = canal === 'email';
  // ⚠️ LE WALLET RÉDIGE UN MESSAGE COURT, comme le SMS — et l'étape 2 le disait déjà dans son titre
  //    (« Message SMS / notification wallet »). Sans cette ligne il n'avait NI l'étape 2 NI la 3 :
  //    une campagne sans message, parce qu'aucune des deux branches ne le nommait.
  const court = sms || canal === 'wallet_push';

  /**
   * LES ÉTAPES QUI S'APPLIQUENT — leur NOMBRE dépend du canal (maquette).
   * 1 canal · 2 message SMS · 3 e-mail · 4 audience · 5 moment. On ne rédige jamais un e-mail pour
   * une campagne SMS, ni l'inverse.
   */
  const etapes: number[] = [1, ...(court ? [2] : []), ...(email ? [3] : []), 4, 5];
  const rang = etapes.indexOf(etape) + 1;
  const dernier = etape === 5;

  const titreEtape = etape === 1 ? t('Le canal : où part le message, avec quels crédits')
    : etape === 2 ? t('Le message SMS / wallet')
      : etape === 3 ? t('L’email : gabarit, contenu, aperçu')
        : etape === 4 ? t('À qui : l’audience') : t('Quand : le moment de l’envoi');

  // Les audiences enregistrées, relues à l'ouverture : une liste périmée ferait reprendre un
  // ciblage qu'on vient de corriger dans un autre onglet.
  useEffect(() => {
    if (!open) return undefined;
    let vivant = true;
    api.savedAudiences()
      .then((r) => { if (vivant) setAudiences(r.rows); })
      // Une liste illisible n'empêche pas de composer une campagne : on n'en propose aucune.
      .catch(() => { if (vivant) setAudiences([]); });
    return () => { vivant = false; };
  }, [open]);

  // ⚠️ LA BIBLIOTHÈQUE EST RELUE À L'OUVERTURE, et son échec se DIT sous le sélecteur : une liste vide
  //    parce que la lecture a échoué laisserait croire que le magasin n'a aucun gabarit (§23.0).
  useEffect(() => {
    if (!open) return undefined;
    let vivant = true;
    api.emailTemplates('campaign')
      .then((r) => { if (vivant) { setBibliotheque(r.rows); setBibliErreur(null); } })
      .catch((e: unknown) => { if (vivant) { setBibliotheque([]); setBibliErreur(motif(e)); } });
    return () => { vivant = false; };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    let vivant = true;
    api.campaignCredits(canal)
      .then((r) => { if (vivant) setCredits(r.credits); })
      // Un solde illisible n'empêche pas d'écrire : on n'affiche rien plutôt qu'une erreur en travers.
      .catch(() => { if (vivant) setCredits(null); });
    return () => { vivant = false; };
  }, [open, canal]);

  const filtres = useCallback((): CampaignFilters => filtresDe(crit), [crit]);

  // ⚠️ LE COMPTE SUIT LES CRITÈRES, sur l'étape qui les pose : c'est la tuile verte de la maquette,
  //    et elle ne peut pas rester sur un chiffre périmé pendant qu'on change un filtre.
  useEffect(() => {
    if (!open || etape !== 4) return undefined;
    let vivant = true;
    setTotal(null);
    const timer = setTimeout(() => {
      api.campaignAudience(canal, filtres())
        .then((r) => { if (vivant) { setTotal(r.total); setMinTemoin(r.minHoldout); } })
        .catch(() => { if (vivant) setTotal(null); });
    }, 350);
    return () => { vivant = false; clearTimeout(timer); };
  }, [open, etape, canal, filtres]);

  const insererFusion = (jeton: string) => {
    const z = zoneMsg.current;
    const i = z ? z.selectionStart : msg.length;
    const j = z ? z.selectionEnd : msg.length;
    setMsg(`${msg.slice(0, i)}${jeton}${msg.slice(j)}`);
    requestAnimationFrame(() => { if (z) { z.focus(); z.setSelectionRange(i + jeton.length, i + jeton.length); } });
  };

  /**
   * Crée la campagne PUIS FIGE SON AUDIENCE avec les critères saisis.
   *
   * ⚠️ LE GEL N'EST PAS OPTIONNEL : sans lui, les six critères de l'étape « à qui » seraient saisis,
   * comptés à l'écran, puis JETÉS — une option offerte sans effet (§23.0). C'est aussi le geste qui
   * transforme un compte indicatif en liste de destinataires nommés.
   * ⚠️ L'ENVOI, LUI, RESTE AU DÉTAIL DE LA CAMPAGNE : il débite des crédits et ne se rattrape pas. Le
   * bouton final dit donc ce qu'il fait réellement, pas ce que le dessin espérait.
   */
  /**
   * CRÉE UN MODÈLE — celui qui engendrera les campagnes, tour après tour.
   *
   * ⚠️ 🔴 CE N’EST PAS UNE CAMPAGNE PROGRAMMÉE, ET LA NUANCE EST TOUT LE DISPOSITIF. Chaque tour
   * produit une campagne NEUVE, avec son compte rendu, sa fenêtre d’attribution et son propre tirage
   * de témoin. Rejouer une même campagne accumulerait les trois : « 2 480 partis » sur combien de
   * mois ? douze fenêtres pour un seul objet ? et les MÊMES personnes témoins à vie.
   *
   * ⚠️ ON N’ENREGISTRE PAS L’AUDIENCE ICI, contrairement à l’envoi immédiat : le modèle PORTE déjà ses
   * critères, et les enregistrer en plus créerait deux copies de la même question, qui divergeraient
   * dès la première correction.
   */
  const creerModele = async () => {
    const recurrence = recurrenceDeSaisie({ quand, dateEnvoi, heureEnvoi, cadence, jours });
    if (!recurrence) {
      showToast(quand === 'once'
        ? t('Choisissez une date et une heure d’envoi.')
        : t('Choisissez au moins un jour, et une heure d’envoi.'), 'err');
      return;
    }
    setBusy(true);
    try {
      const { record } = await api.createCampaignTemplate({
        // ⚠️ 🔴 LA MÊME FONCTION QUE L'ENVOI IMMÉDIAT : un modèle n'a que `subject` et `body`, donc le titre et
        //    le bouton n'atteignent le client QUE composés dans le corps. Ce site envoyait le corps BRUT —
        //    le défaut déjà corrigé pour l'envoi immédiat, resté sur son jumeau.
        ...campaignInput({
          name: nom, channel: canal, short: court,
          shortMessage: msg, composedBody: corpsComplet, subject: email ? objet : null,
        }),
        ...filtres(),
        holdoutPct: pctTemoin,
        recurrence,
      });
      showToast(t('« {name} » est en service — prochain envoi le {v}.', { name: record.name, v: quandLisible(record.nextDueAt) }), 'ok');
      await enregistrerDansBibliotheque();
      onClose();
      setEtape(1); setNom(''); setMsg(''); appliquerModele('annonce'); setEnregistrerGabarit(false); setNomGabarit('');
      setCrit(CRITERES_VIDES); setTotal(null);
      setPctTemoin(0); setNomAudience(''); setIgnores([]); setAudienceChoisie(null); setEnregistrerAudience(false); setQuand('now'); setDateEnvoi(''); setJours([1]);
    } catch (e) {
      showToast(t('Modèle refusé : {v}', { v: motif(e) }), 'err');
    } finally { setBusy(false); }
  };

  const creer = async () => {
    setBusy(true);
    try {
      // ⚠️ 🔴 `corpsComplet` ET NON `corps` : la campagne n'a que `subject` et `body` en base, donc le
      //    titre et l'appel à l'action n'atteignent le destinataire QUE parce qu'ils sont composés
      //    dans le corps. Envoyer le corps brut — ce que faisait ce site — les jetait en silence,
      //    après les avoir fait saisir et dessiner dans l'aperçu (§23.0).
      const input = campaignInput({
        name: nom, channel: canal, short: court,
        shortMessage: msg, composedBody: corpsComplet, subject: email ? objet : null,
      });
      const { record } = await api.createCampaign(input);
      // ⚠️ L'AUDIENCE S'ENREGISTRE AVANT LE GEL, et son échec ne doit PAS emporter la campagne :
      //    l'exploitant vient d'écrire son message et de composer sa cible. Un nom refusé se corrige
      //    au prochain envoi ; une campagne perdue se recommence en entier.
      if (enregistrerAudience && nomAudience.trim()) {
        try {
          await api.saveAudience(nomAudience.trim(), filtres());
        } catch (e) {
          showToast(t('Campagne créée, mais l’audience n’a pas été enregistrée : {v}', { v: motif(e) }), 'err');
        }
      }
      const { record: fige } = await api.freezeCampaign(record.id, filtres(), pctTemoin);
      showToast(t('« {name} » créée — audience figée, prête à partir.', { name: fige.name }), 'ok');
      await enregistrerDansBibliotheque();
      onCree(fige);
      onClose();
      setEtape(1); setNom(''); setMsg(''); appliquerModele('annonce'); setEnregistrerGabarit(false); setNomGabarit('');
      setCrit(CRITERES_VIDES); setTotal(null);
      setPctTemoin(0); setNomAudience(''); setIgnores([]); setAudienceChoisie(null); setEnregistrerAudience(false);
    } catch (e) {
      showToast(t('Création refusée : {v}', { v: motif(e) }), 'err');
    } finally { setBusy(false); }
  };

  // ⚠️ ÉCHAP FERME, ET LE FOCUS ENTRE PUIS REVIENT AU DÉCLENCHEUR : au seul pointeur, la modale ne se quittait pas au clavier.
  const { panelRef } = useOverlayPanel({ open, onClose });
  if (!open) return null;

  // ⚠️ UN SEUL CALCUL DU PARTAGE, rendu deux fois (la phrase et l'avertissement) : le refaire dans
  //    chaque branche du rendu donnerait deux arrondis à corriger le jour où la règle change.
  const partage = partageDAudience(total, pctTemoin, minTemoin);

  const suivant = () => {
    if (etape === 1 && !nom.trim()) { showToast(t('Un nom est requis — c’est lui que vous relirez dans la liste.'), 'err'); return; }
    // ⚠️ L'INTERRUPTEUR DEMANDE UN NOM : une audience « sans titre » se perdrait dans une liste où l'on choisit par le nom.
    if (etape === 4 && enregistrerAudience && !nomAudience.trim()) {
      showToast(t('Donnez un nom à l’audience, ou désactivez « Enregistrer l’audience ».'), 'err'); return;
    }
    const i = etapes.indexOf(etape);
    if (i < etapes.length - 1) setEtape(etapes[i + 1]!);
    else if (quand === 'now') void creer();
    else void creerModele();
  };
  const retour = () => {
    const i = etapes.indexOf(etape);
    if (i > 0) setEtape(etapes[i - 1]!);
  };

  const soldeSms = credits && credits.available !== null ? Number(credits.available) : null;
  const canaux: { v: Canal; l: string; n: string; ic: string; dis: boolean; crL: string; motif?: string }[] = [
    { v: 'sms', l: channelLabel('sms'), n: channelUnit('sms'), ic: IC.sms, dis: soldeSms !== null && soldeSms <= 0, crL: soldeSms === null ? t('solde non mesuré') : soldeSms <= 0 ? t('0 crédit — commander') : t('{n} crédit restant', { n: Math.floor(soldeSms) }) },
    // ⚠️ LE WALLET EST BRANCHÉ DEPUIS QUE LE SERVEUR LE SERT, et la tuile cesse donc d'être grisée.
    //    Elle portait « canal non branché » avec son motif — c'était vrai, et ça ne l'est plus : un
    //    contrôle laissé gris cacherait une fonction livrée (§23.0), exactement comme pour les
    //    audiences enregistrées avant lui.
    // ⚠️ « sans décompte » DIT CE QU'ON SAIT : Eldowallet n'expose aucune réserve décomptable. On
    //    n'écrit pas « gratuit » — savoir s'il facture la notification reste une question ouverte
    //    auprès du fournisseur, et l'affirmer serait une promesse qu'on ne peut pas tenir.
    { v: 'wallet_push', l: channelLabel('wallet_push'), n: channelUnit('wallet_push'), ic: IC.wallet, dis: false, crL: t('sans décompte') },
    { v: 'email', l: channelLabel('email'), n: channelUnit('email'), ic: IC.mail, dis: false, crL: t('sans décompte') },
  ];

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 950, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
      role="dialog" aria-modal="true" aria-label={t('Nouvelle campagne')}>
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'rgba(12,14,16,.5)' }} aria-hidden />
      <div ref={panelRef} style={{
        position: 'relative', width: etape === 3 ? 920 : etape === 4 ? 860 : 640, maxWidth: '96vw', maxHeight: '90vh',
        background: 'var(--paper)', borderRadius: 'var(--r-lg)', boxShadow: '0 28px 70px -10px rgba(0,0,0,.45)',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        {/* En-tête : titre, sous-titre d'étape, points de progression, fermeture. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '16px 22px', borderBottom: '1px solid var(--ink-200)' }}>
          <span style={{ flex: 1 }}>
            <span style={{ display: 'block', fontSize: 'var(--t-h3)', fontWeight: 700, color: 'var(--ink-900)' }}>{t('Nouvelle campagne')}</span>
            <span style={{ display: 'block', fontSize: 'var(--t-dense)', color: 'var(--ink-500)', marginTop: 1 }}>{titreEtape}</span>
          </span>
          <span style={{ display: 'flex', gap: 5 }}>
            {etapes.map((e, i) => (
              <span key={e} style={{ width: 22, height: 5, borderRadius: 'var(--r-pill)', background: i < rang ? 'var(--teal-500)' : 'var(--ink-200)' }} />
            ))}
          </span>
          <button type="button" onClick={onClose} aria-label={t('Fermer')}
            style={{ width: 34, height: 34, border: 'none', borderRadius: 'var(--r-ctrl)', background: 'var(--ink-100)', color: 'var(--ink-600)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
            <Ic d={IC.croix} size={15} w={2.2} />
          </button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 22px' }}>
          {/* ══ 1 — LE CANAL ═══════════════════════════════════════════════════════════════════ */}
          {etape === 1 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <span>
                <span id={`${fieldId}-8`} style={etiq}>{t('Nom de la campagne')}</span>
                <input aria-labelledby={`${fieldId}-8`} value={nom} onChange={(e) => setNom(e.target.value)} placeholder={t('Ex. Rentrée 2026 — coupon 5 €')}
                  style={{ ...champ38, height: 40, padding: '0 13px', fontSize: 'var(--t-title)', fontWeight: 600 }} />
              </span>
              <span>
                <span style={{ ...etiq, marginBottom: 6 }}>{t('Canal — SMS + wallet ensemble, ou email seul : la nature du message diffère. Inactif ou sans crédits = grisé.')}</span>
                <span style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10 }}>
                  {canaux.map((c) => {
                    const on = canal === c.v;
                    return (
                      <button key={c.v} type="button" disabled={c.dis} title={c.motif}
                        onClick={() => { if (!c.dis) { setCanal(c.v); setTotal(null); } }}
                        style={{
                          position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
                          padding: '16px 10px 13px', borderRadius: 'var(--r-md)', textAlign: 'center',
                          border: `1.5px solid ${on ? 'var(--teal-500)' : 'var(--ink-200)'}`,
                          background: on ? 'var(--teal-100)' : 'var(--paper)',
                          cursor: c.dis ? 'not-allowed' : 'pointer', opacity: c.dis ? 0.5 : 1,
                        }}>
                        <span style={{ width: 40, height: 40, borderRadius: 'var(--r-sm)', display: 'flex', alignItems: 'center', justifyContent: 'center', background: on ? 'var(--teal-500)' : 'var(--ink-100)', color: on ? '#fff' : 'var(--ink-600)' }}>
                          <Ic d={c.ic} size={19} w={1.8} />
                        </span>
                        <span style={{ fontSize: 'var(--t-title)', fontWeight: 700, color: on ? 'var(--teal-800)' : 'var(--ink-900)' }}>{c.l}</span>
                        <span style={{ fontSize: 'var(--t-sub)', color: 'var(--ink-500)' }}>{c.n}</span>
                        <span style={{ height: 19, padding: '0 8px', borderRadius: 'var(--r-pill)', background: 'var(--ink-100)', color: 'var(--ink-600)', fontSize: 'var(--t-micro)', fontWeight: 700, display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap' }}>{c.crL}</span>
                        {on && (
                          <span style={{ position: 'absolute', top: 8, right: 8, width: 20, height: 20, borderRadius: 'var(--r-pill)', background: 'var(--teal-500)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Ic d="M5 12l5 5L20 7" size={11} w={3} />
                          </span>
                        )}
                      </button>
                    );
                  })}
                </span>
              </span>
              {sms && soldeSms !== null && soldeSms <= 0 && (
                <span style={ambre}>
                  <Ic d={IC.alerte} size={13} w={2} />
                  <span>{t('Aucun crédit SMS sur ce compte — l’envoi ne partira pas.')}</span>
                </span>
              )}
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', paddingTop: 2 }}>
                <button type="button" disabled title={t(SANS_MOTEUR.credits)}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 7, height: 36, padding: '0 14px', border: '1px solid var(--teal-400)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--teal-800)', fontSize: 'var(--t-body)', fontWeight: 700, cursor: 'not-allowed', opacity: 0.5 }}>
                  <Ic d={IC.panier} size={14} />{t('Commander des crédits en ligne')}
                </button>
                <span style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-400)' }}>{t('SMS & emails — achat direct, sans passer par Orgazone.')}</span>
              </div>
            </div>
          )}

          {/* ══ 2 — LE MESSAGE SMS / WALLET ════════════════════════════════════════════════════ */}
          {etape === 2 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <span>
                <span id={`${fieldId}-9`} style={etiq}>{t('Message SMS / notification wallet')}</span>
                <textarea aria-labelledby={`${fieldId}-9`} ref={zoneMsg} value={msg} onChange={(e) => setMsg(e.target.value)} rows={4}
                  placeholder={t('Bonjour {prénom}, …')}
                  style={{ width: '100%', padding: '11px 13px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', fontSize: 'var(--t-lead)', color: 'var(--ink-900)', lineHeight: 1.55, resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }} />
                <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 7, alignItems: 'center' }}>
                  <span style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-400)' }}>{t('Champs de fusion :')}</span>
                  {FUSION.map((f) => (
                    <button key={f} type="button" disabled={!FUSION_REMPLIE.has(f)}
                      {...(FUSION_REMPLIE.has(f) ? {} : { title: t(SANS_MOTEUR.bon) })}
                      onClick={() => insererFusion(f)}
                      style={{ height: 24, padding: '0 9px', border: '1px dashed var(--ink-300)', borderRadius: 'var(--r-pill)', background: 'var(--ink-50)', color: 'var(--ink-600)', fontFamily: 'var(--font-mono)', fontSize: 'var(--t-sub)', cursor: FUSION_REMPLIE.has(f) ? 'pointer' : 'not-allowed', opacity: FUSION_REMPLIE.has(f) ? 1 : 0.5 }}>{f}</button>
                  ))}
                </span>
                {msg.length > 0 && COMPTEUR[canal] && (
                  <span style={{ ...note, padding: '8px 11px', fontSize: 'var(--t-meta)', marginTop: 7, gap: 7 }}>
                    <Ic d={sms ? IC.sms : IC.wallet} size={12} w={2} />
                    <span>{COMPTEUR[canal]!(msg)}</span>
                  </span>
                )}
              </span>
              <button type="button" disabled role="switch" aria-checked={false} title={t(SANS_MOTEUR.bon)}
                style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '10px 12px', border: '1px dashed var(--ink-200)', borderRadius: 'var(--r-sm)', background: 'transparent', cursor: 'not-allowed', textAlign: 'left', width: '100%', opacity: 0.55 }}>
                <Bascule on={false} />
                <span style={{ fontSize: 'var(--t-lead)', fontWeight: 600, color: 'var(--ink-700)' }}>{t('Joindre un bon — l’attribution devient prouvable en caisse')}</span>
              </button>
            </div>
          )}

          {/* ══ 3 — L'E-MAIL ═══════════════════════════════════════════════════════════════════ */}
          {etape === 3 && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 290px', gap: 22, alignItems: 'start' }}>
              <div style={colonne}>
                <span>
                  <span id={`${fieldId}-0`} style={etiq}>{t('Partir d’un gabarit')}</span>
                  <select aria-labelledby={`${fieldId}-0`} value={gabarit} onChange={(e) => appliquerModele(e.target.value)} style={select38}>
                    <option value="annonce">{t('Annonce simple')}</option>
                    {/* ⚠️ GRISÉ, avec son motif dans le libellé : un <option> n'affiche pas d'infobulle. */}
                    <option value="bon" disabled>{t('Bon mis en avant — à venir')}</option>
                    <option value="vierge">{t('Vierge')}</option>
                    {bibliotheque.map((g) => <option key={g.id} value={`saved:${g.id}`}>{g.name}</option>)}
                  </select>
                  {bibliErreur ? (
                    <span style={{ display: 'block', marginTop: 5, fontSize: 'var(--t-meta)', color: 'var(--amber-fg)' }}>
                      {t('La bibliothèque de gabarits n’a pas pu être lue : {v}', { v: bibliErreur })}
                    </span>
                  ) : null}
                </span>
                <span>
                  <span id={`${fieldId}-1`} style={etiq}>{t('Objet')}</span>
                  <input aria-labelledby={`${fieldId}-1`} value={objet} onChange={(e) => setObjet(e.target.value)} style={{ ...champ38, padding: '0 12px' }} />
                </span>
                <span>
                  <span id={`${fieldId}-2`} style={etiq}>{t('Titre')}</span>
                  <input aria-labelledby={`${fieldId}-2`} value={titre} onChange={(e) => setTitre(e.target.value)} style={{ ...champ38, padding: '0 12px', fontWeight: 600 }} />
                </span>
                <span>
                  <span style={etiq}>{t('Corps')}</span>
                  {/* ⚠️ 🔴 L’ÉDITEUR EST PARTAGÉ (`@orgazone/ui-web`) : il vivait ici, et le second
                      consommateur arrive (bibliothèque de gabarits, courrier de la Console). Deux
                      éditeurs divergeraient à la première correction — et c’est la mise en forme
                      d’un message envoyé à des clients.
                      ⚠️ LES PASTILLES DE FUSION ARRIVENT AVEC LUI : jusque-là elles n’existaient que
                      sur le SMS, et composer un e-mail obligeait à taper « {prénom} » à la main. */}
                  <EmailEditor
                    key={corpsCle}
                    initialValue={corps}
                    onChange={setCorps}
                    fields={CHAMPS_EDITEUR.map((c) => (c.reason ? { token: c.token, reason: t(c.reason) } : { token: c.token }))}
                    imageReason={t(SANS_MOTEUR.image)}
                    ariaLabel={t('Corps de l’e-mail')}
                  />
                </span>
                <span style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                  <button type="button" onClick={() => setBoutonOn((v) => !v)} role="switch" aria-checked={boutonOn}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 9, height: 38, padding: 0, border: 'none', background: 'transparent', cursor: 'pointer' }}>
                    <Bascule on={boutonOn} />
                    <span style={{ fontSize: 'var(--t-body)', fontWeight: 600, color: 'var(--ink-700)' }}>{t('Bouton d’action')}</span>
                  </button>
                  {boutonOn && (
                    <>
                      <input value={boutonL} onChange={(e) => setBoutonL(e.target.value)} placeholder={t('Texte du bouton')}
                        style={{ ...champ38, width: 170, padding: '0 12px' }} />
                      <input value={boutonUrl} onChange={(e) => setBoutonUrl(e.target.value)}
                        placeholder={t('https://… — la page vers laquelle le bouton envoie')}
                        style={{ ...champ38, padding: '0 12px' }} />
                    </>
                  )}
                  {/* ⚠️ Le dessin en fait un INTERRUPTEUR suivi de son champ de nom, pas un bouton. Le gabarit part
                      dans la bibliothèque du magasin À LA CRÉATION de la campagne. */}
                  <button type="button" onClick={() => setEnregistrerGabarit((v) => !v)} role="switch" aria-checked={enregistrerGabarit}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 9, height: 38, padding: 0, border: 'none', background: 'transparent', cursor: 'pointer' }}>
                    <Bascule on={enregistrerGabarit} />
                    <span style={{ fontSize: 'var(--t-dense)', fontWeight: 600, color: 'var(--ink-700)' }}>{t('Enregistrer le gabarit')}</span>
                  </button>
                  {enregistrerGabarit ? (
                    <input value={nomGabarit} onChange={(e) => setNomGabarit(e.target.value)} placeholder={t('Nom du gabarit')}
                      style={{ ...champ38, flex: 1, minWidth: 180, padding: '0 12px', fontSize: 'var(--t-body)' }} />
                  ) : null}
                </span>
                <button type="button" disabled title={t(SANS_MOTEUR.bon)}
                  style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '10px 12px', border: '1px dashed var(--ink-200)', borderRadius: 'var(--r-sm)', background: 'transparent', cursor: 'not-allowed', textAlign: 'left', width: '100%', opacity: 0.55 }}>
                  <Bascule on={false} />
                  <span style={{ fontSize: 'var(--t-lead)', fontWeight: 600, color: 'var(--ink-700)' }}>{t('Joindre un bon — l’attribution devient prouvable en caisse')}</span>
                </button>
              </div>

              {/* L'aperçu en direct — la colonne de 290 px du dessin, à ses mesures. */}
              <div style={{ position: 'sticky', top: 0 }}>
                <div style={{ fontSize: 'var(--t-sub)', fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--ink-400)', marginBottom: 6, textAlign: 'center' }}>{t('Aperçu en direct')}</div>
                {/* ⚠️ 🔴 CE BLOC RENDAIT CE QUE L'ENVOI NE POSE PAS, ET SUR QUATRE POINTS : un en-tête
                    de marque (« SLIDERZ », dans un back-office MULTI-MÉTIER où le commerçant n'est pas
                    Sliderz), un titre et un bouton que la création JETAIT, et un pied de désinscription
                    que l'e-mail ne porte pas. Il rend désormais le corps RÉELLEMENT composé, fusionné
                    avec des valeurs d'exemple — donc l'aperçu et l'envoi ne peuvent plus diverger. */}
                <div style={{ border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', background: 'var(--paper)', overflow: 'hidden', boxShadow: '0 8px 24px -12px rgba(12,14,16,.25)' }}>
                  <div style={{ padding: '13px 14px 6px', fontSize: 'var(--t-sub)', color: 'var(--ink-400)' }}>
                    {t('Objet :')} {objet ? mergeText(objet, apercu.champs, apercu.valeurs) : '—'}
                  </div>
                  <div style={{ padding: '6px 14px 14px', fontSize: 'var(--t-dense)', color: 'var(--ink-700)', lineHeight: 1.55, minHeight: 34 }}
                    dangerouslySetInnerHTML={{ __html: mergeHtml(corpsComplet, apercu.champs, apercu.valeurs) }} />
                </div>
                <div style={{ marginTop: 8, fontSize: 'var(--t-sub)', color: 'var(--ink-400)', lineHeight: 1.5 }}>
                  {t('Aperçu avec des valeurs d’exemple : {v}. Un logiciel de messagerie peut le rendre autrement.', { v: apercu.phrase })}</div>
                {/* ⚠️ 🔴 DIT PLUTÔT QUE SIMULÉ. L'ancien pied de page promettait « se désinscrire en un
                    clic » ; l'envoi e-mail ne pose AUCUN lien de ce genre — seul le SMS ajoute sa
                    mention, et son code explique pourquoi. Le taire laisserait croire que c'est couvert. */}
                {email && (
                  <div style={{ marginTop: 8, fontSize: 'var(--t-sub)', color: 'var(--amber-fg)', lineHeight: 1.5 }}>
                    {t('Ce message ne porte aucun lien de désinscription : écrivez-en un dans le corps si votre envoi en demande.')}</div>
                )}
              </div>
            </div>
          )}

          {/* ══ 4 — À QUI ══════════════════════════════════════════════════════════════════════ */}
          {etape === 4 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 210px', gap: 16, alignItems: 'start' }}>
              <EtapeAudience
                crit={crit}
                onChange={(c) => { setCrit(c); setAudienceChoisie(null); }}
                audiences={audiences}
                audienceChoisie={audienceChoisie}
                onRepartir={(a) => {
                  // ⚠️ REPARTIR REMPLACE LES CRITÈRES, il ne les complète pas : deux ciblages superposés donneraient une
                  //    audience que personne n'a composée.
                  if (!a) { setCrit(CRITERES_VIDES); setIgnores([]); setAudienceChoisie(null); return; }
                  const lu = criteresDe(a.filters);
                  setCrit(lu.criteres); setIgnores(lu.ignores); setAudienceChoisie(a);
                  setNomAudience(a.name); setEnregistrerAudience(false);
                }}
                ignores={ignores}
                enregistrer={enregistrerAudience}
                onEnregistrer={setEnregistrerAudience}
                nomAudience={nomAudience}
                onNomAudience={setNomAudience}
              />

              <div style={{ position: 'sticky', top: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
                {/* La tuile verte du dessin : le compte, et à qui il part. */}
                <div style={{ padding: 15, borderRadius: 'var(--r-md)', background: 'var(--teal-100)', textAlign: 'center' }}>
                  {/* ⚠️ LE COMPTE ET SA LÉGENDE SONT UNE SEULE PHRASE COMPTÉE : en deux éléments, elle disait « 1 clients
                      ciblés ». Pendant la mesure, les points de suspension ne comptent rien — la légende y reste au pluriel. */}
                  {total === null
                    ? <><div style={AUDIENCE_FIGURE}>…</div><div style={AUDIENCE_CAPTION}>{t('clients ciblés')}</div></>
                    : <Trans k='<1>{n}</1> <2>client ciblé</2>' vars={{ n: total }} parts={[
                        (x) => <div style={AUDIENCE_FIGURE}>{x}</div>,
                        (x) => <div style={AUDIENCE_CAPTION}>{x}</div>,
                      ]} />}
                  <div style={{ height: 1, background: 'var(--teal-300)', margin: '10px 0' }} />
                  <div style={{ fontSize: 'var(--t-meta)', color: 'var(--teal-700)', lineHeight: 1.7, textAlign: 'left' }}>
                    {channelReachability(canal)}
                  </div>
                </div>

                {/* ── LE GROUPE TÉMOIN ───────────────────────────────────────────────────────────
                    ⚠️ 🔴 IL VIT ICI, contre le compte, et nulle part ailleurs : c'est le seul endroit de l'application où
                    l'on voit EN MÊME TEMPS combien de gens sont ciblés et combien ne recevront rien.
                    ⚠️ QUATRE CHOIX, PAS UN CHAMP LIBRE : 37 % ne dit rien de plus que 40 %, et un champ libre invite la
                    faute de frappe sur une décision qui coûte des envois. */}
                <div style={{ padding: 13, borderRadius: 'var(--r-md)', border: '1px solid var(--teal-400)', background: 'var(--paper)', display: 'flex', flexDirection: 'column', gap: 9 }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                    <Icon name="flask" size={15} stroke={1.9} color="var(--teal-700)" style={{ flexShrink: 0 }} />
                    <span style={{ fontSize: 'var(--t-body)', fontWeight: 700, color: 'var(--ink-900)' }}>{t('Groupe témoin')}</span>
                  </span>
                  <span style={{ display: 'flex', padding: 2, gap: 2, borderRadius: 'var(--r-ctrl)', background: 'var(--ink-100)', border: '1px solid var(--ink-200)' }}>
                    {[0, 5, 10, 20].map((v) => (
                      <button aria-pressed={pctTemoin === v} key={v} type="button" onClick={() => setPctTemoin(v)}
                        style={{
                          flex: 1, height: 26, padding: '0 9px', border: 'none', borderRadius: 'var(--r-xs)', cursor: 'pointer',
                          fontSize: 'var(--t-note)', fontWeight: 600, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums',
                          background: pctTemoin === v ? 'var(--paper)' : 'transparent',
                          color: pctTemoin === v ? 'var(--ink-900)' : 'var(--ink-500)',
                          boxShadow: pctTemoin === v ? '0 1px 4px rgba(12,14,16,.14)' : 'none',
                        }}>
                        {v === 0 ? t('Aucun') : `${v} %`}
                      </button>
                    ))}
                  </span>
                  {/* ⚠️ LE NOMBRE QUI COMPTE EST CELUI QUI NE PARTIRA PAS. « 10 % » se lit comme un réglage anodin ;
                      « 124 ne recevront rien » est la même chose dite honnêtement, et c'est la seule formulation sur
                      laquelle on peut décider. */}
                  {pctTemoin > 0 && total !== null && partage.temoins > 0 && partage.servis > 0 && (
                    <span style={{ fontSize: 'var(--t-dense)', color: 'var(--ink-800)', lineHeight: 1.45 }}>
                      {t('{servis}, {temoins} — c’est en les comparant qu’on saura ce que la campagne a réellement causé.', { servis: t('{n} recevra le message', { n: partage.servis }), temoins: t('{n} ne recevra rien', { n: partage.temoins }) })}
                    </span>
                  )}
                  {partage.avertissement && (
                    <span role="alert" style={{ padding: '7px 9px', borderRadius: 'var(--r-sm)', background: 'var(--amber-bg)', color: 'var(--amber-fg)', fontSize: 'var(--t-note)', fontWeight: 600, lineHeight: 1.4 }}>
                      {partage.avertissement}
                    </span>
                  )}
                  {/* ⚠️ LE POURQUOI EST TOUJOURS LÀ, y compris sans témoin : c'est à ce moment-là qu'on doit pouvoir
                      décider d'en prendre un. */}
                  <span style={{ fontSize: 'var(--t-note)', color: 'var(--ink-500)', lineHeight: 1.45 }}>
                    {pctTemoin > 0 ? t(TEMOINS_RETENUS) : t(POURQUOI_TEMOIN)}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* ══ 5 — QUAND ══════════════════════════════════════════════════════════════════════ */}
          {etape === 5 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {([
                ['now', t('Envoyer maintenant'), t('Part tout de suite aux joignables — irréversible une fois parti.'), IC.eclair, true, ''],
                ['once', t('Programmer une date'), t('Part tout seul à la date et à l’heure choisies — une seule fois. La cible est recalculée au moment de l’envoi.'), IC.agenda, true, ''],
                ['repeat', t('Répéter'), t('Part tout seul à heure fixe, chaque semaine ou chaque mois. Chaque tour engendre une campagne traçable, avec sa propre cible.'), IC.boucle, true, ''],
              ] as const).map(([v, l, d, ic, dispo, pourquoi]) => {
                const on = quand === v && dispo;
                return (
                  <button key={v} type="button" disabled={!dispo} title={dispo ? undefined : pourquoi}
                    onClick={() => { if (dispo) setQuand(v as 'now' | 'once' | 'repeat'); }}
                    style={{
                      display: 'flex', alignItems: 'flex-start', gap: 12, padding: '13px 14px',
                      borderRadius: 'var(--r-sm)', textAlign: 'left', width: '100%',
                      border: `1.5px solid ${on ? 'var(--teal-500)' : 'var(--ink-200)'}`,
                      background: on ? 'var(--teal-100)' : 'var(--paper)',
                      cursor: dispo ? 'pointer' : 'not-allowed', opacity: dispo ? 1 : 0.5,
                    }}>
                    <span style={{ width: 34, height: 34, borderRadius: 'var(--r-ctrl)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, background: on ? 'var(--teal-500)' : 'var(--ink-100)', color: on ? '#fff' : 'var(--ink-600)' }}>
                      <Ic d={ic} size={16} />
                    </span>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ display: 'block', fontSize: 'var(--t-title)', fontWeight: 700, color: 'var(--ink-900)' }}>{l}</span>
                      <span style={{ display: 'block', fontSize: 'var(--t-note)', color: 'var(--ink-500)', marginTop: 2, lineHeight: 1.5 }}>{d}</span>
                    </span>
                  </button>
                );
              })}

              {/* ══ LA DATE D’UN ENVOI UNIQUE ══════════════════════════════════════════════ */}
              {/* ⚠️ DATE ET HEURE LOCALES DU MAGASIN, envoyées telles quelles : c’est le serveur qui
                  les convertit avec `store.timezone`. Calculer un instant ici ferait partir le
                  message à l’heure de celui qui a cliqué — un SMS de Paris arriverait à 4 h du matin
                  à La Réunion. */}
              {quand === 'once' && (
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end', padding: '12px 14px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)', background: 'var(--paper)' }}>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                    <span style={etiq}>{t('Date d’envoi')}</span>
                    <input type="date" value={dateEnvoi} onChange={(e) => setDateEnvoi(e.target.value)}
                      style={{ height: 36, padding: '0 11px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-900)', fontSize: 'var(--t-lead)', fontFamily: 'inherit' }} />
                  </label>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                    <span style={etiq}>{t('Heure (locale du magasin)')}</span>
                    <input type="time" value={heureEnvoi} onChange={(e) => setHeureEnvoi(e.target.value)}
                      style={{ height: 36, padding: '0 11px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-900)', fontSize: 'var(--t-lead)', fontFamily: 'inherit' }} />
                  </label>
                  <span style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-500)', flex: '1 1 220px', lineHeight: 1.5 }}>
                    
                    {t('L’heure est celle du magasin. Si le serveur manque ce tour, l’envoi part au prochain jour d’ouverture, à la même heure — jamais au milieu de la nuit.')}
                  </span>
                </div>
              )}

              {/* ══ LA CADENCE D’UNE RÉPÉTITION ════════════════════════════════════════════ */}
              {quand === 'repeat' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '12px 14px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)', background: 'var(--paper)' }}>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                    {([['monthly', t('Chaque mois')], ['weekly', t('Chaque semaine')]] as const).map(([v, l]) => (
                      <button key={v} type="button"
                        // ⚠️ CHANGER DE CADENCE REMET LES JOURS À ZÉRO : « le 15 » n’a aucun sens en
                        //    jours de semaine, et « samedi » n’en a aucun en quantièmes. Garder la
                        //    liste ferait envoyer une valeur que le serveur refuse — ou pire, accepte.
                        onClick={() => { setCadence(v); setJours([]); }}
                        style={{ height: 32, padding: '0 14px', borderRadius: 'var(--r-pill)', cursor: 'pointer', fontSize: 'var(--t-note)', fontWeight: 700,
                          border: `1px solid ${cadence === v ? 'var(--teal-500)' : 'var(--ink-200)'}`,
                          background: cadence === v ? 'var(--teal-100)' : 'var(--paper)',
                          color: cadence === v ? 'var(--teal-800)' : 'var(--ink-600)' }}>{l}</button>
                    ))}
                    <label style={{ display: 'flex', alignItems: 'center', gap: 7, marginLeft: 'auto' }}>
                      <span style={etiq}>à</span>
                      <input type="time" value={heureEnvoi} onChange={(e) => setHeureEnvoi(e.target.value)}
                        style={{ height: 32, padding: '0 10px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-900)', fontSize: 'var(--t-note)', fontFamily: 'inherit' }} />
                    </label>
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {/* ⚠️ PLUSIEURS JOURS, PAS UN SEUL : « le 1er et le 15 » est un besoin courant, et
                        une liste à choix unique obligerait à créer deux modèles pour un seul message. */}
                    {(cadence === 'weekly'
                      ? ['L', 'M', 'M', 'J', 'V', 'S', 'D'].map((l, k) => ({ v: k + 1, l }))
                      : Array.from({ length: 31 }, (_, k) => ({ v: k + 1, l: String(k + 1) }))
                    ).map(({ v, l }) => {
                      const on = jours.includes(v);
                      return (
                        <button key={v} type="button"
                          onClick={() => setJours((x) => (on ? x.filter((y) => y !== v) : [...x, v].sort((a, b) => a - b)))}
                          style={{ minWidth: 32, height: 30, padding: '0 8px', borderRadius: 'var(--r-ctrl)', cursor: 'pointer', fontSize: 'var(--t-note)', fontWeight: 700,
                            border: `1px solid ${on ? 'var(--teal-500)' : 'var(--ink-200)'}`,
                            background: on ? 'var(--teal-100)' : 'var(--paper)',
                            color: on ? 'var(--teal-800)' : 'var(--ink-600)' }}>{l}</button>
                      );
                    })}
                  </div>
                  <span style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-500)', lineHeight: 1.5 }}>
                    {/* ⚠️ LES DEUX PHRASES QUI ÉVITENT UNE MAUVAISE SURPRISE, et elles décrivent un
                        comportement RÉEL du serveur — le 31 ramené à la fin du mois, et la cible
                        recalculée à chaque tour. */}
                    {cadence === 'monthly'
                      ? t('Un 29, 30 ou 31 choisi part le dernier jour des mois plus courts — février compris.')
                      : t('Le message part ces jours-là, à l’heure indiquée, tant que le modèle est en service.')}
                    {' '}{t('La cible est recalculée à chaque tour : un « SMS du mois » vise bien les gens du mois en cours.')}
                  </span>
                  <span style={{ ...note, padding: '8px 11px', fontSize: 'var(--t-meta)', gap: 7 }}>
                    <Ic d={IC.info} size={12} w={2} />
                    <span>{t(SANS_MOTEUR.parPersonne)}</span>
                  </span>
                </div>
              )}
              {sms && soldeSms !== null && total !== null && soldeSms < total && (
                <div style={{ ...rose, marginTop: 4 }}>
                  <Ic d={IC.alerte} size={14} w={2} />
                  <span>
                    {/* ⚠️ CETTE PHRASE DISAIT « l’envoi ne partira pas » (corrigé le 2026-09-09) : c’était
                        FAUX, et faux précisément dans le cas où elle s’affiche. Rien ne refuse un envoi
                        pour cause de solde — ni ce bouton, ni la route d’envoi, qui ne consulte jamais
                        les crédits. Avec 400 crédits pour 500 destinataires, 400 messages PARTENT et
                        400 crédits sont débités. Annoncer un blocage inexistant ferait croire à
                        l’exploitant que rien n’a été envoyé (§23.0). */}
                    <Trans k='<1>Crédits insuffisants — une partie seulement sera envoyée.</1> {credits} pour {destinataires} : {derniers} chez l’opérateur. Rechargez avant d’envoyer, changez de canal, ou resserrez la cible.' vars={{ credits: t('{n} crédit', { n: Math.floor(soldeSms) }), destinataires: t('{n} destinataire', { n: total }), derniers: t('les {n} derniers messages échoueront', { n: Math.max(0, total - Math.floor(soldeSms)) }) }} parts={[(x) => <strong>{x}</strong>]} /></span>
                </div>
              )}
              <div style={{ ...note, marginTop: 4 }}>
                <Ic d={IC.info} size={13} w={2} />
                {/* ⚠️ 🔴 CETTE NOTE AFFIRMAIT « 3 messages max par client et par mois […] jamais
                    sur-sollicité » — recopié de la maquette, et FAUX : rien ne compte les messages
                    reçus par personne, aucun envoi n'en saute un seul. C'est le pire des mensonges
                    d'écran (§23.0), parce qu'il porte sur une PROTECTION : l'exploitant lance un
                    troisième envoi du mois en croyant ses clients à l'abri de la sur-sollicitation.
                    ⚠️ Et le pied de l'écran Marketing disait déjà la vérité, dix lignes de commentaire
                    plus loin : le même back-office affirmait une chose et son contraire (forme A). */}
                <span><Trans k='Capping : <1>à venir</1> — {capping}' vars={{ capping: t(SANS_MOTEUR.capping) }} parts={[(x) => <strong>{x}</strong>]} /></span>
              </div>
            </div>
          )}
        </div>

        {/* Pied : retour à gauche, action à droite — sur `ink-50`, comme le dessin. */}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'space-between', padding: '14px 22px', borderTop: '1px solid var(--ink-200)', background: 'var(--ink-50)' }}>
          {rang > 1 ? (
            <button type="button" onClick={retour} disabled={busy}
              style={{ height: 40, padding: '0 18px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)', background: 'var(--paper)', color: 'var(--ink-700)', fontSize: 'var(--t-title)', fontWeight: 600, cursor: 'pointer' }}>{t('Retour')}</button>
          ) : <span />}
          <button type="button" onClick={suivant} disabled={busy}
            style={{ height: 40, padding: '0 18px', border: 'none', borderRadius: 'var(--r-sm)', background: 'var(--teal-500)', color: '#fff', fontSize: 'var(--t-title)', fontWeight: 600, cursor: 'pointer' }}>
            {/* ⚠️ LE BOUTON DIT CE QU’IL FAIT RÉELLEMENT : figer une audience et engendrer un modèle
                sont deux gestes différents, et l’exploitant doit savoir lequel il déclenche. */}
            {dernier
              ? (busy ? t('Enregistrement…') : quand === 'now' ? t('Créer et figer l’audience') : t('Mettre en service'))
              : t('Continuer')}
          </button>
        </div>
      </div>
    </div>
  );
}
